import { useId, useMemo, useState } from 'react';
import { useLanguage } from '@/i18n/LanguageProvider';
import { localeTagFor } from '@/i18n/locale';
import { localizeExerciseName } from '@/lib/fitnessApi';
import { fitnessExerciseProgressCopy } from '@/lib/fitnessExerciseProgressCopy';
import { buildExerciseProgress, exerciseProgressIdentity, exerciseProgressInRange, exerciseProgressRecords,
  type ExerciseProgressPoint, type ExerciseProgressRange } from '@/lib/fitness/exerciseProgress';
import type { WorkoutSessionRow } from '@/lib/fitness/sessionKind';

interface Props {
  ownerId: string;
  sessions: readonly WorkoutSessionRow[];
  exercise?: { name: string; muscle?: string; timed?: boolean };
  asOf?: number;
}
type Metric = 'maxLoadKg' | 'maxReps' | 'maxSeconds' | 'volumeKg';
const control = 'min-h-11 rounded-xl border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary';

export default function ExerciseProgressPanel({ ownerId, sessions, exercise, asOf = Date.now() }: Props) {
  const { lang, t } = useLanguage();
  const copy = fitnessExerciseProgressCopy(lang);
  const id = useId();
  const [selection, setSelection] = useState('');
  const [range, setRange] = useState<ExerciseProgressRange>(30);
  const [metric, setMetric] = useState<Metric>('maxLoadKg');
  const [expanded, setExpanded] = useState({ key: '', limit: 20 });
  const entries = useMemo(() => buildExerciseProgress(sessions, ownerId, asOf), [sessions, ownerId, asOf]);
  const fixed = exercise ? exerciseProgressIdentity(exercise.name, exercise.muscle, exercise.timed) : null;
  const entry = fixed ? entries.find(item => item.key === fixed.key) : entries.find(item => item.key === selection) ?? entries[0];
  const points = useMemo(() => exerciseProgressInRange(entry?.points ?? [], range, asOf), [entry, range, asOf]);
  const records = useMemo(() => exerciseProgressRecords(points), [points]);
  const locale = localeTagFor(lang);
  const numberFormat = useMemo(() => new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }), [locale]);
  const dateFormat = useMemo(() => new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }), [locale]);
  const number = (value: number) => numberFormat.format(value);
  const date = (value: string) => dateFormat.format(new Date(value));
  const labels: Record<Metric, string> = { maxLoadKg: copy.maxLoad, maxReps: copy.maxReps, maxSeconds: copy.maxSeconds, volumeKg: copy.volume };
  const units: Record<Metric, string> = { maxLoadKg: t('fitness.weightUnit'), maxReps: t('fitness.workout.reps'), maxSeconds: t('fitness.ux.seconds'), volumeKg: t('fitness.weightUnit') + '·' + t('fitness.workout.reps') };
  const available = (entry?.timed ? ['maxSeconds'] : ['maxLoadKg', 'maxReps', 'volumeKg']) as Metric[];
  const drawable = available.filter(key => points.some(point => point[key] !== null));
  const selectedMetric = drawable.includes(metric) ? metric : drawable[0];
  const listKey = ownerId + ':' + (entry?.key ?? '') + ':' + range;
  const limit = expanded.key === listKey ? expanded.limit : 20;
  const visible = [...points].reverse().slice(0, limit);
  const recordKeys = available.filter(key => records[key]);

  return <section aria-labelledby={id + '-title'} className="mb-4 rounded-2xl border border-border bg-card p-4 sm:p-5">
    <header className="mb-4">
      <h2 id={id + '-title'} className="font-display text-lg font-semibold text-foreground">{copy.title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{copy.intro}</p>
    </header>
    {!exercise && entries.length > 0 && <div className="mb-4">
      <label htmlFor={id + '-exercise'} className="mb-2 block text-xs font-medium text-muted-foreground">{copy.chooseExercise}</label>
      <select id={id + '-exercise'} value={entry?.key ?? ''} onChange={event => setSelection(event.target.value)} className={control + ' w-full'}>
        {entries.map(item => <option value={item.key} key={item.key}>{localizeExerciseName(item.name, t, lang)}</option>)}
      </select>
    </div>}
    {!entry ? <p role="status" className="py-4 text-sm text-muted-foreground">{copy.empty}</p> : <>
      <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label={copy.range}>
        {([30, 90, 'all'] as const).map(value => <button type="button" key={value} aria-pressed={range === value}
          onClick={() => setRange(value)} className={control + (range === value ? ' border-primary bg-primary/10 font-semibold text-primary' : '')}>
          {value === 30 ? copy.last30 : value === 90 ? copy.last90 : copy.all}
        </button>)}
      </div>
      <p className="mb-4 text-xs text-muted-foreground">{copy.completedOnly} {range === 'all' && copy.historyScope}</p>
      {points.length === 0 ? <p role="status" className="py-4 text-sm text-muted-foreground">{copy.emptyRange}</p> : <>
        <h3 className="mb-3 text-sm font-semibold text-foreground">{copy.records}</h3>
        <dl className="mb-5 grid gap-4 sm:grid-cols-3">
          {recordKeys.map(key => {
            const record = records[key]!;
            return <div key={key} className="border-l-2 border-primary/50 pl-3">
              <dt className="text-xs text-muted-foreground">{labels[key]}</dt>
              <dd className="mt-1 font-display text-xl font-semibold tabular-nums text-foreground">{number(record.value)} <span className="text-xs font-normal text-muted-foreground">{units[key]}</span></dd>
              {key === 'maxReps' && record.loadKg !== undefined && <dd className="mt-1 text-xs text-muted-foreground">{record.loadKg > 0 ? number(record.loadKg) + ' ' + t('fitness.weightUnit') : copy.bodyweight}</dd>}
              <dd className="mt-1 text-xs text-muted-foreground"><time dateTime={record.completedAt}>{date(record.completedAt)}</time></dd>
            </div>;
          })}
        </dl>
        {selectedMetric && <div className="mb-5">
          <label htmlFor={id + '-metric'} className="mb-2 block text-xs font-medium text-muted-foreground">{copy.metric}</label>
          <select id={id + '-metric'} value={selectedMetric} onChange={event => setMetric(event.target.value as Metric)} className={control + ' mb-3 w-full sm:w-auto'}>
            {drawable.map(key => <option key={key} value={key}>{labels[key]} ({units[key]})</option>)}
          </select>
          <ExerciseProgressChart points={points} metric={selectedMetric} label={labels[selectedMetric] + ' (' + units[selectedMetric] + ')'} number={number} date={date} />
        </div>}
        <h3 className="mb-3 text-sm font-semibold text-foreground">{copy.historyTitle}</h3>
        <div className="overflow-x-auto">
          <table className={"w-full text-left text-xs tabular-nums" + (entry.timed ? "" : " min-w-[360px]")}>
            <thead className="text-muted-foreground"><tr>
              <th scope="col" className="pb-3 pr-3 font-medium">{copy.date}</th>
              <th scope="col" className="pb-3 pr-3 font-medium">{copy.sets}</th>
              {!entry.timed && <th scope="col" className="pb-3 pr-3 font-medium">{copy.maxLoad} ({units.maxLoadKg})</th>}
              <th scope="col" className="pb-3 pr-3 font-medium">{entry.timed ? copy.maxSeconds : copy.maxReps}</th>
              {!entry.timed && <th scope="col" className="pb-3 font-medium">{copy.volume} ({units.volumeKg})</th>}
            </tr></thead>
            <tbody>{visible.map(point => <tr key={point.sessionId} className="border-t border-border">
              <th scope="row" className="py-3 pr-3 font-normal text-foreground"><time dateTime={point.completedAt}>{date(point.completedAt)}</time></th>
              <td className="py-3 pr-3">{number(point.sets)}</td>
              {!entry.timed && <td className="py-3 pr-3">{point.maxLoadKg === null ? copy.bodyweight : number(point.maxLoadKg)}</td>}
              <td className="py-3 pr-3">{number((entry.timed ? point.maxSeconds : point.maxReps)!)}</td>
              {!entry.timed && <td className="py-3">{point.volumeKg === null ? '—' : number(point.volumeKg)}</td>}
            </tr>)}</tbody>
          </table>
        </div>
        {points.length > limit && <button type="button" className={control + ' mt-3 text-primary'} onClick={() => setExpanded({ key: listKey, limit: limit + 20 })}>{copy.showMore}</button>}
      </>}
      {!entry.timed && <p className="mt-4 text-xs leading-relaxed text-muted-foreground">{copy.zeroLoadHint} {copy.volumeHint}</p>}
    </>}
  </section>;
}

function ExerciseProgressChart({ points, metric, label, number, date }: {
  points: readonly ExerciseProgressPoint[]; metric: Metric; label: string;
  number: (value: number) => string; date: (value: string) => string;
}) {
  const values = points.flatMap(point => point[metric] === null ? [] : [{ at: point.at, value: point[metric]!, date: point.completedAt }]);
  if (!values.length) return null;
  const min = Math.min(...values.map(point => point.value)), max = Math.max(...values.map(point => point.value));
  const first = values[0], last = values[values.length - 1];
  const xy = values.map(point => ({
    x: first.at === last.at ? 280 : 58 + (point.at - first.at) / (last.at - first.at) * 448,
    y: min === max ? 72 : 124 - (point.value - min) / (max - min) * 104,
  }));
  return <figure aria-label={label}>
    <svg viewBox="0 0 520 145" className="h-40 w-full" role="img" aria-label={label}>
      <title>{label}</title>
      <line x1="58" x2="506" y1="124" y2="124" className="stroke-border" />
      {min !== max && <line x1="58" x2="506" y1="20" y2="20" className="stroke-border" />}
      <text x="0" y={min === max ? '76' : '24'} className="fill-muted-foreground text-[11px]">{number(max)}</text>
      {min !== max && <text x="0" y="128" className="fill-muted-foreground text-[11px]">{number(min)}</text>}
      {xy.length > 1 && <polyline points={xy.map(point => point.x.toFixed(2) + ',' + point.y.toFixed(2)).join(' ')} fill="none" className="stroke-primary" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />}
      <circle cx={xy[xy.length - 1].x} cy={xy[xy.length - 1].y} r="4" className="fill-primary" />
    </svg>
    <figcaption className="flex justify-between gap-4 text-xs text-muted-foreground"><span>{date(first.date)}</span>{first.date !== last.date && <span>{date(last.date)}</span>}</figcaption>
  </figure>;
}
